// Session adapter: the `maa-*` records (identity, live agent, drawer prefs, conversation, overlay
// prefs), one owner each. See .claude/context/session-identity.md.
import { createRecord, readRecord, useRecord } from './record';

export const MAA_SESSION_KEY = 'maa-session';
export const MAA_LIVE_AGENT_KEY = 'maa-live-agent';
export const MAA_DRAWER_PREFS_KEY = 'maa-drawer-prefs';
export const MAA_CONVERSATION_KEY = 'maa-conversation';
export const MAA_OVERLAY_PREFS_KEY = 'maa-overlay-prefs';

const FALLBACK_SESSION_PREFIX = 'fallback-sessionId';

/** Session id from the `thda.s` cookie, else a unique fallback. */
export const getSessionId = () => {
  if (typeof window !== 'undefined') {
    const cookieSessionId = window?.cookieUtils?.readBrowserCookie('thda.s');
    if (cookieSessionId) {
      return cookieSessionId;
    }
    const uuid = window?.crypto?.randomUUID?.() || Date.now();
    return `${FALLBACK_SESSION_PREFIX}-${uuid}`;
  }
  return `${FALLBACK_SESSION_PREFIX}-${Date.now()}`;
};

// Seeds the split records from the legacy envelope — see
// .claude/context/session-identity.md.
export const readLegacySessionRecord = () => readRecord(MAA_SESSION_KEY);

/**
 * The identity record: session id plus creation timestamp, which the expiry rules read.
 * Identity only: the live-agent and drawer fields have their own records.
 */
export const getDefaultSession = () => ({
  sessionId: getSessionId(),
  timestamp: Date.now(),
});

/** LiveAgent connection default, seeded from any legacy flat record (else fresh). */
export const getDefaultLiveAgentSession = () => {
  const legacy = readLegacySessionRecord();
  return {
    isEscalated: legacy?.isEscalated ?? false,
    conversationId: legacy?.conversationId ?? null,
    caseNumber: legacy?.caseNumber ?? null,
    lastActivityAt: legacy?.lastActivityAt ?? null,
  };
};

/** Shell/drawer prefs default, seeded from any legacy flat record (else fresh). */
export const getDefaultDrawerPrefs = () => {
  const legacy = readLegacySessionRecord();
  return {
    shouldAutoOpen: legacy?.shouldAutoOpen ?? false,
    hasSessionInteractedWithFab: legacy?.hasSessionInteractedWithFab ?? false,
    pagesExpandedFABShownOn: legacy?.pagesExpandedFABShownOn ?? [],
  };
};

/**
 * `lastSessionStart` is the identity `timestamp` that message history last initialized against.
 * Null means it never initialized, which must not count as a new session generation.
 */
export const getDefaultConversation = () => ({ lastSessionStart: null });

/**
 * The overlay test's placement preferences. `wantsOverlay` is the toggle's choice at 1280px and
 * up; `geometry` is `{ left, top, height }` from the shopper's last drag or resize, or null
 * before the first one and after a close.
 */
export const getDefaultOverlayPrefs = () => ({ wantsOverlay: false, geometry: null });

// Only drawerPrefs is crossTab — see .claude/context/session-identity.md.
export const sessionRecord = createRecord({
  key: MAA_SESSION_KEY,
  buildDefault: getDefaultSession,
});

export const liveAgentRecord = createRecord({
  key: MAA_LIVE_AGENT_KEY,
  buildDefault: getDefaultLiveAgentSession,
});

export const drawerPrefsRecord = createRecord({
  key: MAA_DRAWER_PREFS_KEY,
  buildDefault: getDefaultDrawerPrefs,
  crossTab: true,
});

// Written ONLY by message history's init() — see .claude/context/session-identity.md.
export const conversationRecord = createRecord({
  key: MAA_CONVERSATION_KEY,
  buildDefault: getDefaultConversation,
});

// sessionStorage: the preferences last for this tab, across its pages. Writers: ShellProvider
// (`wantsOverlay`), OverlayFrame (saves `geometry`) and useDrawerController's `handleClose`
// (clears `geometry`). See .claude/context/session-identity.md ("maa-overlay-prefs").
export const overlayPrefsRecord = createRecord({
  key: MAA_OVERLAY_PREFS_KEY,
  buildDefault: getDefaultOverlayPrefs,
  storage: 'session',
});

/** A session older than 24h (or with no timestamp) is expired. */
export const isSessionExpired = (sessionTimestamp) => {
  if (!sessionTimestamp) {
    return true;
  }
  const sessionAge = Date.now() - sessionTimestamp;
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
  return sessionAge > TWENTY_FOUR_HOURS;
};

/** Was this id minted by getSessionId's fallback, rather than read from `thda.s`? */
export const isFallbackSessionId = (sessionId) => (
  typeof sessionId === 'string' && sessionId.startsWith(`${FALLBACK_SESSION_PREFIX}-`)
);

// 7-day ceiling for any id kind — see .claude/context/session-identity.md.
export const isSessionStale = (sessionTimestamp) => {
  if (!sessionTimestamp) {
    return true;
  }
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  return (Date.now() - sessionTimestamp) > SEVEN_DAYS;
};

/**
 * Whether to replace the identity record. Call it on mount only, so no user is reset in the
 * middle of a conversation. See .claude/context/session-identity.md
 */
export const shouldResetSession = (session) => {
  if (!session?.sessionId) {
    return true;
  }

  if (isFallbackSessionId(session.sessionId)) {
    return isSessionExpired(session.timestamp);
  }

  // A fallback here means thda.s was unreadable; never downgrade a real id to one.
  const currentSessionId = getSessionId();
  if (!isFallbackSessionId(currentSessionId) && currentSessionId !== session.sessionId) {
    return true;
  }

  return isSessionStale(session.timestamp);
};

/**
 * Has the identity been renewed since message history last initialized? It compares timestamps,
 * because a reset can keep the same sessionId. See .claude/context/session-identity.md
 *
 * @param {number|null} lastSessionStart - `maa-conversation.lastSessionStart`.
 *   Null means history never initialized, and the result is false.
 * @param {number|null|undefined} sessionStart - the current `maa-session.timestamp`.
 */
export const isNewSessionGeneration = (lastSessionStart, sessionStart) => (
  lastSessionStart !== null
  && lastSessionStart !== undefined
  && lastSessionStart !== sessionStart
);

/**
 * Order-independent choice between two identity records, so every tab computes the same answer.
 * A real thda.s id beats a fallback id; two ids of one kind compare lexicographically.
 */
export const preferSession = (mine, theirs) => {
  if (!mine?.sessionId) return theirs?.sessionId ? theirs : null;
  if (!theirs?.sessionId) return mine;
  if (mine.sessionId === theirs.sessionId) return mine;

  const mineIsFallback = isFallbackSessionId(mine.sessionId);
  const theirsIsFallback = isFallbackSessionId(theirs.sessionId);
  if (mineIsFallback !== theirsIsFallback) {
    return mineIsFallback ? theirs : mine;
  }
  return mine.sessionId < theirs.sessionId ? mine : theirs;
};

/**
 * Reconcile this tab's identity with the persisted record; returns the record this tab keeps.
 * Call it on mount only. See .claude/context/session-identity.md
 */
export const reconcileSession = (current) => {
  const persisted = readRecord(MAA_SESSION_KEY);
  if (!persisted?.sessionId) {
    return current;
  }
  return preferSession(current, persisted);
};

/** A per-action id derived from the session id (random when there is none). */
export const createActionId = (sessionId) => {
  if (!sessionId) {
    return `${crypto.randomUUID()}_${Date.now().toString()}`;
  }
  return `${sessionId}_${Date.now().toString()}`;
};

/** React binding for the `maa-session` (identity) record. Returns `[session, update]`. */
export const useMaaSession = () => useRecord(sessionRecord);

/** React binding for the `maa-live-agent` record. Returns `[liveAgent, update]`. */
export const useLiveAgentSession = () => useRecord(liveAgentRecord);

/** React binding for the `maa-drawer-prefs` record. Returns `[drawerPrefs, update]`. */
export const useDrawerPrefs = () => useRecord(drawerPrefsRecord);

/** React binding for the `maa-overlay-prefs` record. Returns `[overlayPrefs, update]`. */
export const useOverlayPrefs = () => useRecord(overlayPrefsRecord);
